import { useEffect, useRef, useSyncExternalStore } from 'react'
import type { Winner } from '@/shared/types'

/* ─── Module-level singleton state ─── */
let visibleWinners: Winner[] = []
let freshId: number | null = null
const queue: Winner[] = []
const seenIds = new Set<number>()
const listeners = new Set<() => void>()
let historyLoaded = false

let tickTimeout: ReturnType<typeof setTimeout> | null = null
let subscriberCount = 0

function notify() {
  for (const fn of listeners) fn()
}

function tick() {
  if (queue.length > 0) {
    const next = queue.shift()!
    visibleWinners = [next, ...visibleWinners]
    freshId = next.id
    notify()
    setTimeout(() => {
      freshId = null
      notify()
    }, 800)
  }
  tickTimeout = setTimeout(tick, 180000 + Math.random() * 60000)
}

function startTicker() {
  if (tickTimeout) return
  tickTimeout = setTimeout(tick, 500)
}

function stopTicker() {
  if (tickTimeout) {
    clearTimeout(tickTimeout)
    tickTimeout = null
  }
}

function subscribe(fn: () => void) {
  listeners.add(fn)
  subscriberCount++
  if (subscriberCount === 1) startTicker()
  return () => {
    listeners.delete(fn)
    subscriberCount--
    if (subscriberCount === 0) stopTicker()
  }
}

function getSnapshot() {
  return visibleWinners
}

function getFreshId() {
  return freshId
}

/* ─── Feed winners from backend ─── */
function feedWinners(winners: Winner[]) {
  if (winners.length === 0) return

  const sorted = [...winners].sort((a, b) => b.deal_number - a.deal_number)

  if (!historyLoaded) {
    // First batch — show history immediately
    historyLoaded = true
    for (const w of sorted) seenIds.add(w.id)
    visibleWinners = sorted
    notify()
    return
  }

  // Subsequent fetches — only queue genuinely new winners
  for (const w of sorted) {
    if (!seenIds.has(w.id)) {
      seenIds.add(w.id)
      queue.push(w)
    }
  }
}

/* ─── Hook ─── */
export function useDripWinners(winners: Winner[]) {
  const prevRef = useRef<Winner[]>([])
  useEffect(() => {
    if (winners !== prevRef.current) {
      prevRef.current = winners
      feedWinners(winners)
    }
  }, [winners])

  const visible = useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
  const highlightId = useSyncExternalStore(
    (fn) => { listeners.add(fn); return () => { listeners.delete(fn) } },
    getFreshId,
    getFreshId,
  )

  return { visibleWinners: visible, freshId: highlightId }
}
